import {
  Requester,
  RequestInterceptor,
  ResponseInterceptor,
  RequestOptions,
  Method,
} from './types';
import { fetchRequster } from './fetchRequester';

export const createHttp = <
  CustomOptions = {},
  Variables = {},
  AdditionalParams = {}
>() => {
  let requester = fetchRequster;
  const variables = {} as Variables;
  const requestInterceptors: RequestInterceptor<
    CustomOptions,
    AdditionalParams
  >[] = [];
  const responseInterceptors: ResponseInterceptor<
    CustomOptions,
    AdditionalParams
  >[] = [];

  const doRequest = async <T>(
    options: RequestOptions<CustomOptions, AdditionalParams>
  ) => {
    requestInterceptors.forEach((interceptor) => {
      options = interceptor(options);
    });

    let response = await requester(options);

    responseInterceptors.forEach((interceptor) => {
      response = interceptor({
        response,
        options,
      });
    });

    if (response.error) {
      throw response.error;
    }

    return response.result as unknown as T;
  };

  return {
    setRequester: (newRequester: Requester) => {
      requester = newRequester;
    },

    setVariable: <Key extends keyof Variables>(
      key: Key,
      value: Variables[Key]
    ) => {
      variables[key] = value;
    },
    getVariable: <Key extends keyof Variables>(key: Key) => variables[key],

    addRequestInterceptor: (
      fn: RequestInterceptor<CustomOptions, AdditionalParams>
    ) => requestInterceptors.push(fn),
    addResponseInterceptor: (
      fn: ResponseInterceptor<CustomOptions, AdditionalParams>
    ) => responseInterceptors.push(fn),

    get: <T>(
      customOptions: CustomOptions,
      url: string,
      params?: RequestOptions<CustomOptions, AdditionalParams>['params']
    ) =>
      doRequest<T>({
        customOptions,
        url,
        params: { ...params, method: Method.get } as any,
      }),

    post: <T>(
      customOptions: CustomOptions,
      url: string,
      params?: RequestOptions<CustomOptions, AdditionalParams>['params']
    ) =>
      doRequest<T>({
        customOptions,
        url,
        params: { ...params, method: Method.post } as any,
      }),

    patch: <T>(
      customOptions: CustomOptions,
      url: string,
      params?: RequestOptions<CustomOptions, AdditionalParams>['params']
    ) =>
      doRequest<T>({
        customOptions,
        url,
        params: { ...params, method: Method.patch } as any,
      }),

    put: <T>(
      customOptions: CustomOptions,
      url: string,
      params?: RequestOptions<CustomOptions, AdditionalParams>['params']
    ) =>
      doRequest<T>({
        customOptions,
        url,
        params: { ...params, method: Method.put } as any,
      }),

    delete: <T>(
      customOptions: CustomOptions,
      url: string,
      params?: RequestOptions<CustomOptions, AdditionalParams>['params']
    ) =>
      doRequest<T>({
        customOptions,
        url,
        params: { ...params, method: Method.delete } as any,
      }),
  };
};
